import { PersistedWorkflow } from '@cotera/sdk/core';
import type { WebhookInstallStatus } from '@cotera/client/app/v1/webhook-providers/types';
import type { WebhookProviderConfigDraft } from '@cotera/client/app/v1/webhook-providers/types';
import {
  createDefaultWebhookProviderConfigDraft,
  parseWebhookProviderKey,
  toWebhookConfigRecord,
} from '@cotera/client/app/v1/webhook-providers/webhook-config-draft';
import type { WorkflowGraphApiData } from '../workflow-graph.types';
import type { WebhookTriggerPreviewState } from './types';

const { PersistedWorkflowTriggerKind } = PersistedWorkflow;

export function readWebhookConfigDraftFromGraph(
  graph: WorkflowGraphApiData,
  triggerId: string
): WebhookProviderConfigDraft {
  for (const snapshot of graph.triggers) {
    if (snapshot.kind !== PersistedWorkflowTriggerKind.Webhook) {
      continue;
    }
    if (snapshot.trigger.id !== triggerId) {
      continue;
    }
    const trigger = snapshot.trigger;
    return {
      name: trigger.name,
      providerKey: parseWebhookProviderKey(trigger.providerKey),
      eventKey: trigger.eventKey ?? null,
      eventConfig: toWebhookConfigRecord(trigger.eventConfig),
      verificationConfig: trigger.verificationConfig ?? {
        profile: 'cotera',
      },
      endpointSecret: trigger.secret ?? '',
      enabled: trigger.enabled,
    };
  }
  return createDefaultWebhookProviderConfigDraft();
}

export function readWebhookInstallStatusFromGraph(
  graph: WorkflowGraphApiData,
  triggerId: string
): WebhookInstallStatus | undefined {
  for (const snapshot of graph.triggers) {
    if (
      snapshot.kind === PersistedWorkflowTriggerKind.Webhook &&
      snapshot.trigger.id === triggerId
    ) {
      return snapshot.trigger.installStatus;
    }
  }
  return undefined;
}

export function readWebhookHasSecretFromGraph(
  graph: WorkflowGraphApiData,
  triggerId: string
): boolean | undefined {
  for (const snapshot of graph.triggers) {
    if (snapshot.kind !== PersistedWorkflowTriggerKind.Webhook) {
      continue;
    }
    if (snapshot.trigger.id !== triggerId) {
      continue;
    }
    const trigger = snapshot.trigger as {
      hasSecret?: boolean;
      secret?: string;
    };
    if (trigger.hasSecret !== undefined) {
      return trigger.hasSecret;
    }
    return trigger.secret !== undefined && trigger.secret !== '';
  }
  return undefined;
}

export function readWebhookPreviewStateFromGraph(
  graph: WorkflowGraphApiData,
  triggerId: string
): WebhookTriggerPreviewState | undefined {
  for (const snapshot of graph.triggers) {
    if (snapshot.kind !== PersistedWorkflowTriggerKind.Webhook) {
      continue;
    }
    if (snapshot.trigger.id !== triggerId) {
      continue;
    }
    const trigger = snapshot.trigger as {
      enabled: boolean;
      installStatus?: WebhookInstallStatus;
      hasSecret?: boolean;
      secret?: string;
    };
    return {
      installStatus: trigger.installStatus ?? 'draft',
      installError: null,
      hasSecret:
        trigger.hasSecret ??
        (trigger.secret !== undefined && trigger.secret !== ''),
      enabled: trigger.enabled,
    };
  }
  return undefined;
}
